import {
  type AuthenticationResult,
  type Configuration,
  type EventMessage,
  EventType,
  PublicClientApplication,
  type RedirectRequest,
} from '@azure/msal-browser';
import { ErrorMonitoring } from '@ui/services/error-monitoring';

import { EnvConfig } from './env';

const CLIENT_ID = EnvConfig.getKey('AD_CLIENT_ID');
const AUTHORITY = EnvConfig.getKey('AD_AUTHORITY_URL');
const SCOPES = EnvConfig.getKey('AD_SCOPES').split(',');
const authorityUrl = new URL(AUTHORITY);
const isLocalAuthority =
  authorityUrl.hostname === 'localhost' ||
  authorityUrl.hostname === '127.0.0.1';

// Config object to be passed to Msal on creation
const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: AUTHORITY,
    ...(isLocalAuthority
      ? {
          protocolMode: 'OIDC' as const,
          knownAuthorities: [authorityUrl.host],
        }
      : {}),
    redirectUri: '/auth/redirect',
    postLogoutRedirectUri: '/logout/redirect',
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'localStorage', // local storage allows us to share cache across tabs
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
};

// Add here scopes for id token to be used at MS Identity Platform endpoints.
export const loginRequest: RedirectRequest = {
  scopes: SCOPES,
};

/**
 * Initialize a PublicClientApplication instance which is provided to the MsalProvider component
 * We recommend initializing this outside of your root component to ensure it is not re-initialized on re-renders
 */
export const msalInstance = new PublicClientApplication(msalConfig);

export async function initAuthProvider() {
  await msalInstance.initialize();

  if (!msalInstance.getActiveAccount()) {
    const accounts = msalInstance.getAllAccounts();

    if (accounts.length === 1) {
      msalInstance.setActiveAccount(accounts[0]);
    } else if (accounts.length > 1) {
      // Cannot tell which cached account should be active; auto-picking the
      // first one risked silently signing in as the wrong user. Clear the
      // cache instead so the app falls through to a fresh login.
      try {
        await msalInstance.clearCache();
      } catch (e) {
        // Don't let a failed cache clear block app init entirely - fall
        // through to a normal login prompt instead of a blank screen.
        ErrorMonitoring.captureException(
          e instanceof Error
            ? e
            : new Error('Failed to clear ambiguous MSAL account cache'),
        );
      }
    }
  }

  msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      const account = payload.account;
      msalInstance.setActiveAccount(account);
    }
  });
}
